/**
 * État d'une macro vis-à-vis de son objectif — **une seule définition**, suivie
 * par le bandeau de la journée, les jauges d'un repas et le compteur de kcal.
 *
 * Trois états, et c'est délibérément peu :
 *
 *   `under`     on n'y est pas encore — teinte d'identité de la macro
 *   `onTarget`  on y est, à ±5 % près — **vert**
 *   `over`      on a dépassé la tolérance — **rouge**
 *
 * ⚠️ **La tolérance est un pourcentage de l'objectif, pas une valeur absolue.**
 * ±5 % de 2 465 kcal fait ±123 kcal, ±5 % de 40 g de lipides fait ±2 g : la même
 * marge en grammes n'aurait aucun sens d'une macro à l'autre.
 *
 * 🔴 **L'état se décide sur la valeur ARRONDIE, celle qui est affichée.** C'est
 * ce qui empêche « −0 g de trop » : un restant de −0,04 g s'affiche « 0 » — le
 * lire comme un dépassement afficherait un signe moins devant un zéro, et
 * annoncerait un dépassement que l'écran ne montre pas. On juge donc ce qu'on
 * montre, jamais la valeur brute.
 *
 * ⚠️ Aucun jugement au-delà de la couleur (specs §6.2, §5.3) : l'application
 * décrit, elle ne corrige pas et ne conseille pas. Le vert dit « atteint », pas
 * « bien ».
 */

import { isOnTarget, ON_TARGET_TOLERANCE } from '../../../shared/macroTarget';
import { roundMacroForDisplay } from '../../../shared/types';

export type MacroStatus = 'under' | 'onTarget' | 'over';

/**
 * Largeur de la bande considérée comme « atteint », en part de l'objectif.
 *
 * 🔴 **Elle vit désormais dans `shared/macroTarget.ts`** (V2 étape 9), parce
 * que le tableau de bord compte **côté serveur** le « % de jours dans la
 * cible » et doit le compter sur la bande que cet écran peint. Réexportée ici
 * pour que les appelants de ce module n'aient pas à savoir qu'elle a déménagé.
 *
 * ⚠️ **Déplacée à l'identique, valeur comprise** — leçon de `chartScale.ts` :
 * ce module est éprouvé sur l'appareil depuis la refonte, et le refaire à
 * l'occasion d'un ajout, c'est le casser à l'occasion d'un ajout.
 */
export { ON_TARGET_TOLERANCE };

export interface MacroReading {
  status: MacroStatus;
  /** Le restant **arrondi**, tel qu'il sera écrit. Négatif = dépassement. */
  displayed: number;
  /** Vrai quand le restant affiché vaut zéro — ni « restant », ni « de trop ». */
  isZero: boolean;
}

/**
 * @param remaining restant brut renvoyé par le serveur (négatif si dépassé)
 * @param target objectif de la macro ; `0` signifie « pas d'objectif »
 * @param unit sert seulement à choisir l'arrondi (kcal entière, gramme au
 *   dixième) — la règle vit dans `shared/types.ts` et est partagée avec le
 *   bilan composé côté serveur depuis l'étape 9
 */
export function readMacro(remaining: number, target: number, unit: string): MacroReading {
  const displayed = roundMacroForDisplay(remaining, unit === 'kcal');
  const isZero = displayed === 0;

  /*
   * 🔴 **Le verdict est rendu par `shared/macroTarget.ts`, plus ici.** Le même
   * jugement est porté côté serveur par le tableau de bord (V2 étape 9) : deux
   * écritures de « à ±5 % près, sur la valeur arrondie » auraient fini par
   * diverger, et le symptôme aurait été un pourcentage de jours atteints
   * calculé sur des journées que cet écran peint en vert.
   *
   * ⚠️ `isOnTarget` rend `null` quand il n'y a pas d'objectif — ni atteint, ni
   * raté. Ici ce cas retombe sur `under`, exactement comme avant : sans
   * objectif il n'y a ni vert ni rouge, et la macro garde sa teinte d'identité.
   */
  const onTarget = isOnTarget(remaining, target, unit === 'kcal');
  if (onTarget === null) return { status: 'under', displayed, isZero };
  if (onTarget) return { status: 'onTarget', displayed, isZero };
  return { status: displayed < 0 ? 'over' : 'under', displayed, isZero };
}

/**
 * Ce qui se lit à côté du chiffre.
 *
 * ⚠️ **« restant » à zéro, jamais « de trop ».** Demandé après usage : un
 * « 0 g de trop » est à la fois faux et désagréable à lire au moment précis où
 * l'objectif est atteint.
 *
 * 🔴 **Aucun signe n'est jamais écrit devant le chiffre**, et c'est pour ça
 * qu'il n'y a pas de fonction pour en produire un. Le mot porte déjà le sens :
 * « 5 g de trop » se lit, « −5 g de trop » dit deux fois la même chose et se
 * lit une fois de travers — le moins semble annoncer un manque alors que la
 * phrase annonce un excès. Les appelants affichent donc toujours la **valeur
 * absolue**.
 */
export function remainingWord(reading: MacroReading): string {
  if (reading.isZero) return 'restant';
  return reading.displayed < 0 ? 'de trop' : 'restant';
}
